<template>
    <!-- Child row under an expanded Barn: a genuine <tr> sharing Index.vue's table columns, one size
         down from BarnRow.vue (no chevron/badge - a Section has no children of its own, and no Trend
         column content - history is only fetched at Barn granularity, a known scope limit).

         Nesting under the Barn is two adjacent zero-width bordered strips inside the Name cell,
         `items-stretch` in a flex row so each strip's height matches the name text's own line-height:
         a gray guide-line (unconditional - Sections only ever render under an expanded Barn, so
         there's always a parent to visually descend from) plus the same coloured deviation-accent
         strip BarnRow's own Name cell uses, kept visually distinct and slightly indented from it
         rather than sharing one edge - the tree guide-line stays, rather than flattening down to
         plain indentation-only like the Farm→Barn level uses. -->
    <tr class="text-sm transition-colors hover:bg-gray-50">
        <td class="py-0.5 pl-6 pr-2">
            <div class="flex items-stretch gap-2">
                <span class="w-0 border-l-2 border-gray-200" aria-hidden="true"></span>
                <span class="w-0 border-l-2" :class="rowClasses" aria-hidden="true"></span>
                <span class="min-w-0 flex-1 truncate text-[13px] font-medium" :class="hasNoValue ? 'text-gray-400' : 'text-gray-700'" :title="nameTooltip">{{ section.name }}</span>
            </div>
        </td>

        <td class="py-0.5 pr-2 tabular-nums">
            <div class="flex items-center gap-2">
                <template v-if="section.currentValue != null && section.resolvedTarget != null">
                    <ObjectiveBar :current-value="section.currentValue" :resolved-target="section.resolvedTarget" :target-source="section.status.target_source" :ceiling="ceiling" class="w-16" />
                    <span class="text-[12px] font-bold leading-none" :class="valueColourClass"
                        >{{ $n(section.currentValue, 'decimal') }}%<span v-if="isBarOverflow(section.currentValue, ceiling)">&raquo;</span></span
                    >
                </template>
                <!-- Measured, but no target anywhere to compare against - Sections stay text-only here
                     (no "Set" button; the Barn headline above already offers the fix once per Farm).
                     Any status message here isn't a data-quality caveat (the value is legit), so it
                     goes on the value itself as a tooltip rather than greying the name. -->
                <template v-else-if="section.currentValue != null">
                    <span class="text-[13px] font-bold leading-none" :class="valueColourClass" :title="section.status.message || undefined">{{ $n(section.currentValue, 'decimal') }}</span>
                    <span class="text-[11px] font-medium" :class="valueColourClass">%</span>
                </template>
                <!-- No measured value at all: a bare dash, the reason lives on the name's tooltip
                     instead of inline text (see BarnRow.vue's own note on why). -->
                <span v-else class="text-[12px] font-medium text-gray-400">{{ placeholder }}</span>
            </div>
        </td>

        <!-- No Trend column content for a Section - history is only fetched at Barn granularity. -->
        <td></td>

        <td class="py-0.5 pr-2 tabular-nums">
            <span
                v-if="section.pigsToTarget != null"
                class="text-[10.5px] font-semibold"
                :class="pigsToTargetColourClass(section.pigsToTarget)"
                :title="pigsToTargetTooltipKey(section.pigsToTarget) ? $tc(`views.objectives.texts.${pigsToTargetTooltipKey(section.pigsToTarget)}`, Math.abs(section.pigsToTarget)) : undefined"
            >
                {{ formatPigsToTarget(section.pigsToTarget) }}
            </span>
        </td>

        <td class="py-0.5 pr-1 text-right tabular-nums">
            <span class="text-[11px] font-medium text-gray-400">{{ $t('views.objectives.texts.deadCountLabel', { count: section.deadCount }) }}</span>
        </td>
    </tr>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { SectionObjectiveRow } from './objectiveRow';
import { objectiveValueColourClass, pigsToTargetColourClass, pigsToTargetTooltipKey, formatPigsToTarget } from './objectiveDisplay';
import { isInDeviation, isWithinTarget } from './deviation';
import { isBarOverflow } from './objectiveBar';
import ObjectiveBar from './ObjectiveBar.vue';

const props = defineProps<{
    section: SectionObjectiveRow;
    ceiling: number;
}>();

const placeholder = '—';

const valueColourClass = computed(() => objectiveValueColourClass(props.section));

// See BarnRow.vue's own note on hasNoValue/nameTooltip - same rule, one level down.
const hasNoValue = computed(() => props.section.currentValue == null);
const nameTooltip = computed(() => (hasNoValue.value && props.section.status.message ? `${props.section.name} — ${props.section.status.message}` : props.section.name));

// Just the deviation-accent strip's colour - see BarnRow.vue's own note on the same computed.
const rowClasses = computed(() => {
    if (isInDeviation(props.section)) return 'border-l-red-400';
    if (isWithinTarget(props.section)) return 'border-l-green-400';
    return 'border-l-gray-100';
});
</script>
